import { z } from 'zod';

/**
 * Contracten van de module floor-reports (Stap 36): melden vanaf de vloer.
 *
 * Een bijna-ongeval, een onveilige situatie, een kwaliteitsafwijking of een defect — gemeld vanaf
 * een telefoon, met foto's en een zone, bij de groep die het hoort op te volgen.
 *
 * Het oude platform had een meldknop zonder enige afhandeling. Daar zit de hele reden van deze
 * module: een melding die nergens terechtkomt, is erger dan geen meldknop, want ze leert mensen
 * dat melden niets doet.
 *
 * Eén keuze bepaalt bijna alles hieronder: **anoniem melden bewaart geen melder.** Niet verborgen,
 * niet versleuteld, niet "alleen voor beheerders" — er is geen rij die een persoon aan de melding
 * koppelt. Wat dat kost (geen terugkoppeling, geen vragen stellen) staat in ADR-0062 en wordt de
 * melder **vooraf** gezegd.
 */

export const reportStatusSchema = z.enum(['new', 'in_progress', 'resolved', 'rejected']);
export type ReportStatus = z.infer<typeof reportStatusSchema>;

/** Wat er in de geschiedenis van een melding staat. */
export const reportEventKindSchema = z.enum(['submitted', 'status_changed', 'comment', 'assigned']);
export type ReportEventKind = z.infer<typeof reportEventKindSchema>;

// --- Meldingstypes -------------------------------------------------------------------

/**
 * Een soort melding, per bedrijf: "Bijna-ongeval", "Defect", "Kwaliteitsafwijking".
 *
 * Elk type heeft zijn eigen behandelgroep, want dat is precies waarom het oude systeem niet
 * werkte: alles kwam op één hoop bij één persoon. Een defect hoort bij onderhoud, een
 * bijna-ongeval bij de preventieadviseur.
 */
/**
 * Het icoon van een soort op de tegels van het meldscherm (VLR-4). Een vaste lijst: een icoon dat
 * de app niet kent, zou een lege tegel worden.
 */
export const reportTypeIconSchema = z.enum([
  'triangle',
  'shield',
  'wrench',
  'flame',
  'droplet',
  'zap',
  'hardhat',
  'leaf',
  'eye',
]);
export type ReportTypeIcon = z.infer<typeof reportTypeIconSchema>;

export const reportTypeSchema = z.object({
  id: z.guid(),
  name: z.string(),
  description: z.string(),
  /** De groep die deze meldingen opvolgt. */
  handlerGroupId: z.guid().nullable(),
  handlerGroupName: z.string().nullable(),
  /**
   * Er is een behandelgroep gekozen, maar die bestaat niet meer: niemand volgt deze meldingen op
   * (hertest 29 okt.). Het beheer toont dan een waarschuwing bij een actieve soort.
   */
  handlerMissing: z.boolean().default(false),
  /** Mag iemand dit type anoniem melden? Per type, want het verschilt echt. */
  anonymousAllowed: z.boolean(),
  /** Of medewerkers dit type kunnen kiezen. Uitzetten in plaats van weghalen. */
  active: z.boolean(),
  openCount: z.number().int(),
  /** Het icoon op de tegel (VLR-4). */
  icon: reportTypeIconSchema,
  /** Een ernstige soort: oranje tegel met het woord "Ernstig", en de uitleg met een oranje rand. */
  severe: z.boolean(),
});
export type ReportType = z.infer<typeof reportTypeSchema>;

export const reportTypeListSchema = z.object({ items: z.array(reportTypeSchema) });
export type ReportTypeList = z.infer<typeof reportTypeListSchema>;

export const createReportTypeRequestSchema = z.object({
  name: z.string().trim().min(1, 'Geef het meldingstype een naam.').max(80),
  description: z.string().trim().max(300).default(''),
  handlerGroupId: z.guid().nullable().default(null),
  anonymousAllowed: z.boolean(),
  icon: reportTypeIconSchema.default('triangle'),
  severe: z.boolean().default(false),
});
export type CreateReportTypeRequest = z.infer<typeof createReportTypeRequestSchema>;

export const updateReportTypeRequestSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  description: z.string().trim().max(300).optional(),
  handlerGroupId: z.guid().nullable().optional(),
  anonymousAllowed: z.boolean().optional(),
  active: z.boolean().optional(),
  icon: reportTypeIconSchema.optional(),
  severe: z.boolean().optional(),
});
export type UpdateReportTypeRequest = z.infer<typeof updateReportTypeRequestSchema>;

// --- Zones ---------------------------------------------------------------------------

/**
 * Een plek in het bedrijf: "Hal 3", "Lijn 2", "Laadkade".
 *
 * Een lijst en geen vrij tekstveld, want "bij de machine" is als locatie onbruikbaar zodra er
 * twintig machines zijn. Wie zijn plek niet in de lijst vindt, schrijft het in de beschrijving —
 * daarom is de zone optioneel en de beschrijving verplicht.
 */
export const zoneSchema = z.object({
  id: z.guid(),
  name: z.string(),
  active: z.boolean(),
  /**
   * De vestiging waar deze zone ligt (Stap 29).
   *
   * Leeg betekent: overal, en dat blijft de standaard voor een bedrijf met één vestiging. Twee
   * fabrieken hebben allebei een "Hal 3", en zonder dit veld is dat één zone - met als gevolg
   * dat een melding uit Kortrijk bij de behandelaar van Gent op de lijst komt.
   */
  siteId: z.guid().nullable().default(null),
});
export type Zone = z.infer<typeof zoneSchema>;

export const zoneListSchema = z.object({ items: z.array(zoneSchema) });
export type ZoneList = z.infer<typeof zoneListSchema>;

export const createZoneRequestSchema = z.object({
  name: z.string().trim().min(1, 'Geef de zone een naam.').max(80),
  /** Leeg laten bij een bedrijf met één vestiging. */
  siteId: z.guid().nullable().default(null),
});
export type CreateZoneRequest = z.infer<typeof createZoneRequestSchema>;

// --- Melden --------------------------------------------------------------------------

export const createReportRequestSchema = z.object({
  typeId: z.guid(),
  zoneId: z.guid().nullable().default(null),
  description: z
    .string()
    .trim()
    .min(5, 'Zeg kort wat je ziet; één woord is voor de behandelaar niet te volgen.')
    .max(2000),
  /** Bestand-ids van foto's, in de volgorde waarin de melder ze toevoegde. */
  fileIds: z.array(z.guid()).max(5).default([]),
  anonymous: z.boolean(),
  /**
   * Een sleutel van de client, zodat tweemaal verzenden één melding blijft.
   *
   * Nodig omdat dit het verzoek is dat het vaakst op een slecht netwerk gedaan wordt: wie op
   * "versturen" drukt en een draaiend wieltje ziet, drukt nog een keer. Zonder deze sleutel staan
   * er dan twee identieke meldingen van een lekkende leiding in de lijst, en moet een behandelaar
   * uitzoeken of het één of twee lekken zijn.
   */
  clientRef: z.string().trim().min(8).max(100),
});
export type CreateReportRequest = z.infer<typeof createReportRequestSchema>;

/** Eén regel uit de geschiedenis van een melding: wie, wanneer, wat. */
export const reportEventSchema = z.object({
  id: z.guid(),
  kind: reportEventKindSchema,
  /** De naam van wie het deed; `null` bij de inzending van een anonieme melding. */
  actorName: z.string().nullable(),
  fromStatus: reportStatusSchema.nullable(),
  toStatus: reportStatusSchema.nullable(),
  comment: z.string(),
  /** Een opmerking die alleen behandelaars zien; de melder leest hem niet. */
  internal: z.boolean(),
  at: z.string().datetime(),
});
export type ReportEvent = z.infer<typeof reportEventSchema>;

export const reportSchema = z.object({
  id: z.guid(),
  typeId: z.guid(),
  typeName: z.string(),
  zoneId: z.guid().nullable(),
  zoneName: z.string().nullable(),
  description: z.string(),
  fileIds: z.array(z.guid()),
  status: reportStatusSchema,
  anonymous: z.boolean(),
  /**
   * De melder. **Altijd `null` bij een anonieme melding**, en niet omdat hij weggelaten wordt in
   * het antwoord: er staat niets in de database (ADR-0062).
   */
  reporterId: z.guid().nullable(),
  reporterName: z.string().nullable(),
  assigneeId: z.guid().nullable(),
  assigneeName: z.string().nullable(),
  submittedAt: z.string().datetime(),
  closedAt: z.string().datetime().nullable(),
  /** De volledige geschiedenis, oudste eerst. Interne opmerkingen alleen voor behandelaars. */
  events: z.array(reportEventSchema),
  /** Mag ík deze melding opvolgen? */
  canHandle: z.boolean(),
});
export type Report = z.infer<typeof reportSchema>;

export const reportListSchema = z.object({ items: z.array(reportSchema) });
export type ReportList = z.infer<typeof reportListSchema>;

export const updateReportRequestSchema = z.object({
  status: reportStatusSchema.optional(),
  /** `null` betekent: niemand meer toegewezen. */
  assigneeId: z.guid().nullable().optional(),
  /**
   * Waarom. Bij afwijzen en oplossen verplicht: dat is de terugkoppeling die de melder leest, en
   * "afgewezen" zonder reden is de reden dat niemand een tweede keer meldt.
   */
  comment: z.string().trim().max(1000).default(''),
});
export type UpdateReportRequest = z.infer<typeof updateReportRequestSchema>;

export const addReportCommentRequestSchema = z.object({
  comment: z.string().trim().min(1, 'Schrijf iets.').max(1000),
  /** Een interne opmerking ziet de melder niet. Standaard zichtbaar: hij meldde iets. */
  internal: z.boolean().default(false),
});
export type AddReportCommentRequest = z.infer<typeof addReportCommentRequestSchema>;

/** Hoeveel foto's er aan één melding mogen hangen. */
export const REPORT_MAX_PHOTOS = 5;
